import React, { lazy, Suspense } from 'react';
import { Route, Navigate } from 'react-router-dom';

/**
 * VikarRoutes — Modular Route Architecture
 * NordicWorld Studio Standards: Tectonic Grid (8px) & Sensory Minimalism
 * Designed for speed. Built for focus.
 *
 * All imports are lazy-loaded for G2 Continuity through code-splitting.
 * Spacing: 8px grid only — no p-5, no m-3, no off-grid values.
 */

// ─── Lazy Imports ────────────────────────────────────────────────────────────
const DashboardVikar          = lazy(() => import('../pages/DashboardVikar.jsx'));
const VikarDagbog             = lazy(() => import('../pages/VikarDagbog.jsx'));
const VikarCalendar           = lazy(() => import('../pages/vikar/VikarCalendar'));
const FindVagter              = lazy(() => import('../pages/vikar/FindVagter'));
const GDPRSettings            = lazy(() => import('../pages/vikar/GDPRSettings'));
const MobileTimesheetSubmission = lazy(() => import('../pages/vikar/MobileTimesheetSubmission').then(m => ({ default: m.MobileTimesheetSubmission })));
const Indstillinger           = lazy(() => import('../pages/vikar/Indstillinger'));
const VikarRequests           = lazy(() => import('../pages/VikarRequests'));
const VikarShifts             = lazy(() => import('../pages/VikarShifts'));
const MineVagter              = lazy(() => import('../pages/MineVagter.jsx'));
const MyTimesheets            = lazy(() => import('../pages/MyTimesheets.jsx'));
const MyRequests              = lazy(() => import('../pages/MyRequests.jsx'));
const MineLonsedler           = lazy(() => import('../pages/vikar/MineLonsedler'));

// ─── Nordic Minimalist Fallback ───────────────────────────────────────────────
/**
 * VikarFallback — Tectonic Grid loading state.
 * padding: 32px (4 × 8px). Spinner: 40px × 40px.
 * Less, but better.
 */
const VikarFallback = () => (
  <div
    role="status"
    aria-label="Indlæser…"
    className="flex min-h-[50vh] items-center justify-center"
    style={{ padding: '32px' }}
  >
    <div
      className="animate-spin rounded-full border-4 border-app-border border-t-app-primary"
      style={{ height: '40px', width: '40px', margin: '0 auto' }}
    />
  </div>
);

// ─── Route Guard Type ─────────────────────────────────────────────────────────
type GuardComponent = React.FC<{ roles?: string[]; children: React.ReactNode }>;

// ─── VikarRoutes ──────────────────────────────────────────────────────────────
/**
 * VikarRoutes — All routes scoped to the VIKAR role.
 *
 * Accepts the Guard component from App.tsx to enforce role-based access.
 * Every route is wrapped in <Suspense fallback={<VikarFallback />}> for
 * seamless lazy loading with G2 Continuity.
 *
 * Tectonic Grid audit: all spacing in child pages must use multiples of 8px.
 * Canonical paths are defined here; legacy aliases redirect to canonical.
 */
export const VikarRoutes = (Guard: GuardComponent) => [

  // ── Core Dashboard ──────────────────────────────────────────────────────────
  <Route
    key="dashboard-vikar"
    path="/dashboard/vikar"
    element={
      <Guard roles={['VIKAR', 'ADMIN']}>
        <Suspense fallback={<VikarFallback />}>
          <DashboardVikar />
        </Suspense>
      </Guard>
    }
  />,

  // ── Dagbog (Journal) ────────────────────────────────────────────────────────
  <Route
    key="vikar-dagbog"
    path="/dashboard/vikar/dagbog"
    element={
      <Guard roles={['VIKAR', 'ADMIN']}>
        <Suspense fallback={<VikarFallback />}>
          <VikarDagbog />
        </Suspense>
      </Guard>
    }
  />,

  // ── Vagtplan / Calendar ─────────────────────────────────────────────────────
  <Route
    key="vikar-vagtplan"
    path="/vikar/vagtplan"
    element={
      <Guard roles={['VIKAR']}>
        <Suspense fallback={<VikarFallback />}>
          <VikarCalendar />
        </Suspense>
      </Guard>
    }
  />,

  // ── Find Vagter (Job Search) ─────────────────────────────────────────────────
  <Route
    key="find-vagter"
    path="/find-vagter"
    element={
      <Guard roles={['VIKAR', 'ADMIN']}>
        <Suspense fallback={<VikarFallback />}>
          <FindVagter />
        </Suspense>
      </Guard>
    }
  />,

  // ── Indstillinger ────────────────────────────────────────────────────────────
  <Route
    key="indstillinger"
    path="/indstillinger"
    element={
      <Guard roles={['VIKAR', 'KOMMUNE', 'ADMIN']}>
        <Suspense fallback={<VikarFallback />}>
          <Indstillinger />
        </Suspense>
      </Guard>
    }
  />,

  // ── GDPR Settings ───────────────────────────────────────────────────────────
  <Route
    key="vikar-gdpr"
    path="/vikar/gdpr"
    element={
      <Guard roles={['VIKAR', 'KOMMUNE', 'ADMIN']}>
        <Suspense fallback={<VikarFallback />}>
          <GDPRSettings />
        </Suspense>
      </Guard>
    }
  />,

  // ── Staffing Requests (Vikar view) ──────────────────────────────────────────
  <Route
    key="vikar-requests"
    path="/vikar/requests"
    element={
      <Guard roles={['VIKAR', 'ADMIN']}>
        <Suspense fallback={<VikarFallback />}>
          <VikarRequests />
        </Suspense>
      </Guard>
    }
  />,

  // ── Assigned Shifts (Vikar view) ────────────────────────────────────────────
  <Route
    key="vikar-shifts"
    path="/vikar/shifts"
    element={
      <Guard roles={['VIKAR', 'ADMIN']}>
        <Suspense fallback={<VikarFallback />}>
          <VikarShifts />
        </Suspense>
      </Guard>
    }
  />,

  // ── Mine Anmodninger (My Requests — legacy Base44 entity view) ──────────────
  <Route
    key="mine-anmodninger"
    path="/mine-anmodninger"
    element={
      <Guard roles={['VIKAR', 'ADMIN']}>
        <Suspense fallback={<VikarFallback />}>
          <MyRequests />
        </Suspense>
      </Guard>
    }
  />,

  // ── Mine Vagter (My Shifts — calendar + list) ────────────────────────────────
  <Route
    key="mine-vagter"
    path="/mine-vagter"
    element={
      <Guard roles={['VIKAR', 'ADMIN']}>
        <Suspense fallback={<VikarFallback />}>
          <MineVagter />
        </Suspense>
      </Guard>
    }
  />,

  // ── My Timesheets ────────────────────────────────────────────────────────────
  <Route
    key="my-timesheets"
    path="/my-timesheets"
    element={
      <Guard roles={['VIKAR', 'ADMIN']}>
        <Suspense fallback={<VikarFallback />}>
          <MyTimesheets />
        </Suspense>
      </Guard>
    }
  />,

  // ── Mine Lønsedler (Salary Slips — vikar view) ─────────────────────────────
  <Route
    key="mine-lonsedler"
    path="/mine-lonsedler"
    element={
      <Guard roles={['VIKAR', 'ADMIN']}>
        <Suspense fallback={<VikarFallback />}>
          <MineLonsedler />
        </Suspense>
      </Guard>
    }
  />,

  // ─────────────────────────────────────────────────────────────────────────────
  // Legacy Aliases — redirect to canonical Vikar paths
  // Preserves backward compatibility with old Base44/Antigravity route structure
  // ─────────────────────────────────────────────────────────────────────────────

  /** /my-requests → /mine-anmodninger (App.tsx legacy route consolidated here) */
  <Route
    key="my-requests-alias"
    path="/my-requests"
    element={<Navigate to="/mine-anmodninger" replace />}
  />,

  /** /my-shifts → /mine-vagter (App.tsx legacy route consolidated here) */
  <Route
    key="my-shifts-alias"
    path="/my-shifts"
    element={<Navigate to="/mine-vagter" replace />}
  />,

  /** Dashboard sub-path aliases */
  <Route
    key="dash-vikar-vagter"
    path="/dashboard/vikar/vagter"
    element={<Navigate to="/mine-vagter" replace />}
  />,
  <Route
    key="dash-vikar-kalender"
    path="/dashboard/vikar/kalender"
    element={<Navigate to="/vikar/vagtplan" replace />}
  />,
  <Route
    key="dash-vikar-profil"
    path="/dashboard/vikar/profil"
    element={<Navigate to="/profile" replace />}
  />,
];
